import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'

const MIN_W = '0'
const DECIMAL_TYPES = ['decimal', 'numeric']

const useStyles = makeStyles((theme) => ({
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.8125rem',
    tableLayout: 'fixed',
    '& th': {
      textAlign: 'left',
      padding: '6px 8px',
      borderBottom: 'var(--border-neutral-thin)',
      fontWeight: 600,
      whiteSpace: 'nowrap'
    },
    '& td': {
      padding: '4px 8px',
      borderBottom: 'var(--border-neutral-thin)',
      verticalAlign: 'middle',
      overflow: 'hidden'
    }
  },
  colOrdinal: { width: '40px' },
  colName: { width: '18%' },
  colType: { width: '12%' },
  colMaxLen: { width: '8%' },
  colPrecision: { width: '8%' },
  colScale: { width: '7%' },
  colCheck: { width: '55px', textAlign: 'center' },
  colDefault: { width: '10%' },
  colActions: { width: '110px' },
  actions: {
    display: 'flex',
    gap: '4px'
  },
  footer: {
    marginTop: theme.layout.gap.g5
  }
}))

function ColumnsEditor ({ columns, onChange, dataTypes }) {
  const classes = useStyles()

  const updateColumn = useCallback((idx, field, value) => {
    const next = columns.map((col, i) => {
      if (i !== idx) return col
      return { ...col, [field]: value }
    })
    onChange(next)
  }, [columns, onChange])

  const addColumn = useCallback(() => {
    onChange([
      ...columns,
      {
        _id: Math.random().toString(36).slice(2),
        columnName: '',
        dataType: 'varchar',
        maxLength: 256,
        isNullable: true,
        isPrimaryKey: false,
        ordinalPosition: columns.length + 1
      }
    ])
  }, [columns, onChange])

  const removeColumn = useCallback((idx) => {
    const next = columns
      .filter((_, i) => i !== idx)
      .map((col, i) => ({ ...col, ordinalPosition: i + 1 }))
    onChange(next)
  }, [columns, onChange])

  const moveColumn = useCallback((idx, direction) => {
    const target = idx + direction
    if (target < 0 || target >= columns.length) return
    const next = [...columns]
    const temp = next[idx]
    next[idx] = next[target]
    next[target] = temp
    onChange(next.map((col, i) => ({ ...col, ordinalPosition: i + 1 })))
  }, [columns, onChange])

  return (
    <div>
      <table className={classes.table}>
        <colgroup>
          <col className={classes.colOrdinal} />
          <col className={classes.colName} />
          <col className={classes.colType} />
          <col className={classes.colMaxLen} />
          <col className={classes.colPrecision} />
          <col className={classes.colScale} />
          <col className={classes.colCheck} />
          <col className={classes.colCheck} />
          <col className={classes.colDefault} />
          <col className={classes.colActions} />
        </colgroup>
        <thead>
          <tr>
            <th>#</th>
            <th>Column Name</th>
            <th>Data Type</th>
            <th>Max Length</th>
            <th>Precision</th>
            <th>Scale</th>
            <th>Nullable</th>
            <th>PK</th>
            <th>Default Extra</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {columns.map((col, idx) => (
            <tr key={col._id || idx}>
              <td>{col.ordinalPosition}</td>
              <td>
                <SydInput
                  minWidth={MIN_W}
                  value={col.columnName}
                  onChange={(e) => updateColumn(idx, 'columnName', e.target.value)}
                  size='sm'
                  placeholder='columnName'
                />
              </td>
              <td>
                <SydSelect
                  minWidth={MIN_W}
                  value={col.dataType}
                  onChange={(e) => updateColumn(idx, 'dataType', e.target.value)}
                  size='sm'
                  options={dataTypes.map(dt => ({ value: dt, label: dt }))}
                />
              </td>
              <td>
                <SydInput
                  minWidth={MIN_W}
                  type='number'
                  value={col.maxLength ?? ''}
                  onChange={(e) => updateColumn(idx, 'maxLength', e.target.value ? parseInt(e.target.value, 10) : null)}
                  size='sm'
                  placeholder='-'
                  disabled={DECIMAL_TYPES.includes(col.dataType)}
                />
              </td>
              <td>
                <SydInput
                  minWidth={MIN_W}
                  type='number'
                  min={1}
                  max={65}
                  value={col.numericPrecision ?? ''}
                  onChange={(e) => updateColumn(idx, 'numericPrecision', e.target.value ? parseInt(e.target.value, 10) : null)}
                  size='sm'
                  placeholder='20'
                  disabled={!DECIMAL_TYPES.includes(col.dataType)}
                />
              </td>
              <td>
                <SydInput
                  minWidth={MIN_W}
                  type='number'
                  min={0}
                  max={30}
                  value={col.numericScale ?? ''}
                  onChange={(e) => updateColumn(idx, 'numericScale', e.target.value ? parseInt(e.target.value, 10) : null)}
                  size='sm'
                  placeholder='8'
                  disabled={!DECIMAL_TYPES.includes(col.dataType)}
                />
              </td>
              <td style={{ textAlign: 'center' }}>
                <input
                  type='checkbox'
                  checked={col.isNullable}
                  onChange={(e) => updateColumn(idx, 'isNullable', e.target.checked)}
                />
              </td>
              <td style={{ textAlign: 'center' }}>
                <input
                  type='checkbox'
                  checked={col.isPrimaryKey}
                  onChange={(e) => updateColumn(idx, 'isPrimaryKey', e.target.checked)}
                />
              </td>
              <td>
                <SydInput
                  minWidth={MIN_W}
                  value={col.defaultExtra || ''}
                  onChange={(e) => updateColumn(idx, 'defaultExtra', e.target.value || undefined)}
                  size='sm'
                  placeholder='-'
                />
              </td>
              <td>
                <div className={classes.actions}>
                  <SydButton variant='outline' size='sm' onClick={() => moveColumn(idx, -1)} disabled={idx === 0}>
                    &#8593;
                  </SydButton>
                  <SydButton variant='outline' size='sm' onClick={() => moveColumn(idx, 1)} disabled={idx === columns.length - 1}>
                    &#8595;
                  </SydButton>
                  <SydButton variant='outline' size='sm' onClick={() => removeColumn(idx)}>
                    &#10005;
                  </SydButton>
                </div>
              </td>
            </tr>
          ))}
          {columns.length === 0 && (
            <tr>
              <td colSpan={10} style={{ textAlign: 'center', padding: '16px', color: 'var(--color-text-secondary)' }}>
                No columns defined. Add columns or use &quot;Reset from Pipeline&quot;.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className={classes.footer}>
        <SydButton variant='outline' size='sm' onClick={addColumn}>
          Add Column
        </SydButton>
      </div>
    </div>
  )
}

ColumnsEditor.propTypes = {
  columns: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  dataTypes: PropTypes.array.isRequired
}

export default ColumnsEditor
